import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { MatchBadge, ConfidenceBadge, ConfidenceLegend } from "@/components/ConfidenceBadge";
import SubscribeCTA from "@/components/SubscribeCTA";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Search, Sparkles, Loader2, FileText, FileSpreadsheet, ArrowRight, ExternalLink, Lock, Globe, Lightbulb, Bot, Bookmark, BookmarkPlus, X as XIcon, Download, SlidersHorizontal, Check, FolderPlus, ListPlus } from "lucide-react";

const QUICK = ["who in my network works in fintech", "connections who could intro me to a VC", "senior people I know in hospitality", "former colleagues in North America", "founders I know building AI startups", "investors for a pre-seed or seed round"];
const ALL = "__all__";

function downloadBlob(data, filename) {
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default function SearchPage() {
  const { user, refreshUser } = useAuth();
  const [params] = useSearchParams();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastQuery, setLastQuery] = useState("");
  const [lastRegion, setLastRegion] = useState(null);
  const [exporting, setExporting] = useState("");
  const [limitHit, setLimitHit] = useState(false);
  const [regions, setRegions] = useState([]);
  const [scope, setScope] = useState(params.get("region") || ALL);
  const [sectorOptions, setSectorOptions] = useState([]);
  const [selectedSectors, setSelectedSectors] = useState([]);
  const [lastSectors, setLastSectors] = useState([]);
  const [sectorQuery, setSectorQuery] = useState("");
  const [saved, setSaved] = useState([]);
  const [savingSearch, setSavingSearch] = useState(false);
  const [segExporting, setSegExporting] = useState("");
  const [selected, setSelected] = useState([]);
  const [lists, setLists] = useState([]);
  const [newListName, setNewListName] = useState("");
  const [addingList, setAddingList] = useState(false);

  const loadSaved = () => api.get("/saved-searches").then(({ data }) => setSaved(data.saved || [])).catch(() => {});
  const loadLists = () => api.get("/lists").then(({ data }) => setLists(data.lists || [])).catch(() => {});

  useEffect(() => {
    api.get("/filters/options").then(({ data }) => { setRegions(data.regions || []); setSectorOptions(data.sectors || []); }).catch(() => {});
    loadSaved();
    loadLists();
  }, []);

  const toggleSector = (s) => setSelectedSectors((cur) => cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);

  const isPaid = user?.is_paid;
  const used = user?.searches_used ?? 0;
  const limit = user?.search_limit ?? 3;
  const remaining = Math.max(0, limit - used);
  const outOfSearches = !isPaid && remaining <= 0;
  const scopeLabel = scope === ALL ? "All regions" : scope;

  const run = async (q) => {
    const term = (q ?? query).trim();
    if (term.length < 2) { toast.error("Type a longer question"); return; }
    if (outOfSearches) { setLimitHit(true); return; }
    setQuery(term); setLoading(true); setLimitHit(false);
    const regionArg = scope === ALL ? null : scope;
    const sectorsArg = selectedSectors;
    try {
      const { data } = await api.post("/search", { query: term, region: regionArg, sectors: sectorsArg });
      setResults(data.results); setMeta(data); setLastQuery(term); setLastRegion(regionArg); setLastSectors(sectorsArg); setSelected([]);
      refreshUser();
      if (data.results.length === 0) toast.info("No strong matches. Try rephrasing your query.");
    } catch (e) {
      if (e.response?.status === 402) { setLimitHit(true); refreshUser(); }
      else toast.error(e.response?.data?.detail || "Search failed");
    } finally { setLoading(false); }
  };

  const doExport = async (fmt) => {
    if (!results?.length) return;
    if (!isPaid) { setLimitHit(true); return; }
    setExporting(fmt);
    const items = results.map((r) => ({ id: r.id, reason: r.reason, match: r.match }));
    try {
      const { data } = await api.post(`/export/${fmt}`, { items, query: lastQuery, include_reasoning: true, scope: "filtered", note: meta?.assistant_note }, { responseType: "blob" });
      downloadBlob(data, fmt === "pdf" ? "linkviewer-shortlist.pdf" : "linkviewer-shortlist.xlsx");
      toast.success(`${fmt.toUpperCase()} exported`);
    } catch { toast.error("Export failed"); } finally { setExporting(""); }
  };

  const saveCurrent = async () => {
    if (!lastQuery) return;
    setSavingSearch(true);
    try {
      await api.post("/saved-searches", { query: lastQuery, region: lastRegion });
      await loadSaved();
      toast.success("Search saved");
    } catch { toast.error("Could not save search"); } finally { setSavingSearch(false); }
  };

  const deleteSaved = async (id) => {
    try { await api.delete(`/saved-searches/${id}`); setSaved((s) => s.filter((x) => x.id !== id)); }
    catch { toast.error("Could not remove"); }
  };

  const runSaved = (s) => {
    setScope(s.region || ALL);
    run(s.query);
  };

  const exportSegment = async (segment, fmt) => {
    if (!isPaid) { setLimitHit(true); return; }
    setSegExporting(`${segment}-${fmt}`);
    try {
      const { data } = await api.post(`/export/segment/${fmt}`, { query: lastQuery, region: lastRegion, sectors: lastSectors, segment }, { responseType: "blob" });
      downloadBlob(data, fmt === "pdf" ? "linkviewer-segment.pdf" : "linkviewer-segment.xlsx");
      toast.success(`Segment ${fmt.toUpperCase()} exported`);
    } catch (e) {
      if (e.response?.status === 402) setLimitHit(true);
      else toast.error("Segment export failed");
    } finally { setSegExporting(""); }
  };

  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const clearSelected = () => setSelected([]);
  const selectedItems = () => results.filter((r) => selected.includes(r.id)).map((r) => ({ id: r.id, reason: r.reason || "", match: r.match || "Medium" }));

  const exportSelected = async (fmt) => {
    if (!isPaid) { setLimitHit(true); return; }
    if (!selected.length) return;
    setExporting(`sel-${fmt}`);
    try {
      const { data } = await api.post(`/export/${fmt}`, { items: selectedItems(), query: lastQuery ? `Selected from: ${lastQuery}` : "Selected contacts", include_reasoning: true, scope: "filtered", note: meta?.assistant_note }, { responseType: "blob" });
      downloadBlob(data, fmt === "pdf" ? "linkviewer-selected.pdf" : "linkviewer-selected.xlsx");
      toast.success(`${fmt.toUpperCase()} exported`);
    } catch (e) { if (e.response?.status === 402) setLimitHit(true); else toast.error("Export failed"); } finally { setExporting(""); }
  };

  const addToList = async (listId) => {
    if (!selected.length) return;
    try { const { data } = await api.post(`/lists/${listId}/items`, { contact_ids: selected }); toast.success(`Added ${data.added} contact${data.added !== 1 ? "s" : ""} to list`); clearSelected(); loadLists(); }
    catch { toast.error("Could not add to list"); }
  };

  const createAndAdd = async () => {
    const name = newListName.trim();
    if (!name) return;
    try { const { data } = await api.post("/lists", { name }); setNewListName(""); setAddingList(false); await addToList(data.id); }
    catch { toast.error("Could not create list"); }
  };

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto animate-fade-up">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-blue-500/25 bg-blue-500/10 text-blue-300 text-xs font-mono-niq uppercase tracking-wider mb-4"><Sparkles className="w-3.5 h-3.5" /> AI query engine</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-2">Ask your network</h1>
          <p className="text-slate-400">Describe what you need, or narrow by region and sector. Ranked, explained shortlist out.</p>
          {!isPaid && (
            <div className="mt-3 inline-flex items-center gap-2 text-xs font-mono-niq px-3 py-1.5 rounded-full border border-[#27334D] bg-[#111726]" data-testid="free-search-counter">
              <span className={remaining > 0 ? "text-slate-300" : "text-amber-400"}>{remaining} of {limit} free searches left</span>
            </div>
          )}
        </div>

        {(limitHit || outOfSearches) && !isPaid && (
          <SubscribeCTA className="mb-8" title="You've used your free searches" subtitle={`Free accounts get ${limit} searches, view-only. Go Pro for unlimited searches and PDF + Excel downloads.`} />
        )}

        <form onSubmit={(e) => { e.preventDefault(); run(); }} className="relative mb-4">
          <Search className="w-5 h-5 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
          <input data-testid="search-query-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="e.g. who in my network works in fintech"
            className="w-full pl-12 pr-32 py-4 rounded-xl bg-[#111726] border border-[#27334D] text-base focus:border-blue-500 focus:outline-none transition-colors" />
          <button data-testid="search-query-submit" disabled={loading} className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Search <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>

        <div className="flex flex-wrap items-center gap-2.5 mb-8" data-testid="search-scope">
          <Globe className="w-4 h-4 text-blue-400 shrink-0" />
          <span className="text-sm text-slate-400">Searching in</span>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger data-testid="search-scope-trigger" className="w-auto min-w-[150px] h-9 bg-[#111726] border-[#27334D] text-slate-100 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#111726] border-[#27334D] text-slate-100">
              <SelectItem value={ALL} data-testid="scope-option-all">All regions</SelectItem>
              {regions.map((r) => (
                <SelectItem key={r} value={r} data-testid={`scope-option-${r}`}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {sectorOptions.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="sector-filter-trigger" className="flex items-center gap-2 h-9 px-3 rounded-md bg-[#111726] border border-[#27334D] text-sm text-slate-100 hover:border-slate-500 transition-colors">
                  <SlidersHorizontal className="w-3.5 h-3.5 text-blue-400" />
                  {selectedSectors.length === 0 ? "All sectors" : `${selectedSectors.length} sector${selectedSectors.length > 1 ? "s" : ""}`}
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-72 p-0 bg-[#111726] border-[#27334D] text-slate-100">
                <div className="flex items-center justify-between px-3 py-2.5 border-b border-[#27334D]">
                  <span className="text-xs uppercase tracking-wider font-mono-niq text-slate-400">Filter by sector</span>
                  {selectedSectors.length > 0 && (
                    <button onClick={() => setSelectedSectors([])} data-testid="sector-filter-clear" className="text-xs text-blue-400 hover:text-blue-300">Clear</button>
                  )}
                </div>
                <div className="p-2 border-b border-[#27334D]">
                  <input
                    value={sectorQuery}
                    onChange={(e) => setSectorQuery(e.target.value)}
                    data-testid="sector-filter-search"
                    placeholder="Search sectors…"
                    className="w-full bg-[#0D121C] border border-[#27334D] rounded-md px-2.5 py-1.5 text-sm outline-none focus:border-blue-500"
                  />
                </div>
                <ScrollArea className="h-56">
                  <div className="p-1.5">
                    {sectorOptions.filter((s) => s.toLowerCase().includes(sectorQuery.toLowerCase())).map((s) => (
                      <label key={s} data-testid={`sector-option-${s}`} className="flex items-center gap-2.5 px-2.5 py-2 rounded-md hover:bg-white/[0.04] cursor-pointer">
                        <Checkbox checked={selectedSectors.includes(s)} onCheckedChange={() => toggleSector(s)} className="border-slate-500 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" />
                        <span className="text-sm truncate">{s}</span>
                      </label>
                    ))}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
          )}

          {(scope !== ALL || selectedSectors.length > 0) && (
            <span className="text-xs text-slate-500 font-mono-niq hidden sm:inline">search is scoped to your selection</span>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {QUICK.map((q) => (
            <button key={q} onClick={() => run(q)} data-testid={`quick-prompt-${q.replace(/\s+/g, '-')}`} className="text-xs px-3 py-1.5 rounded-full border border-[#27334D] text-slate-400 hover:text-slate-100 hover:border-slate-500 transition-colors">{q}</button>
          ))}
        </div>

        {saved.length > 0 && (
          <div className="mb-8" data-testid="saved-searches">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-mono-niq text-slate-500 mb-2"><Bookmark className="w-3.5 h-3.5 text-blue-400" /> Saved searches</div>
            <div className="flex flex-wrap gap-2">
              {saved.map((s) => (
                <div key={s.id} data-testid={`saved-search-${s.id}`} className="group flex items-center gap-1.5 text-xs pl-3 pr-1.5 py-1.5 rounded-full border border-[#27334D] bg-[#111726] hover:border-blue-500/50 transition-colors">
                  <button onClick={() => runSaved(s)} data-testid={`saved-search-run-${s.id}`} className="text-slate-300 hover:text-white text-left max-w-[240px] truncate">{s.query}{s.region ? ` · ${s.region}` : ""}</button>
                  <button onClick={() => deleteSaved(s.id)} data-testid={`saved-search-delete-${s.id}`} title="Remove" className="p-1 rounded-full text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"><XIcon className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading && (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => <div key={i} className="h-24 rounded-xl bg-[#111726] border border-[#1E2A3F] animate-pulse" />)}
          </div>
        )}

        {!loading && results !== null && (
          <div>
            {meta?.assistant_note && (
              <div data-testid="assistant-note" className="rounded-2xl border border-blue-500/30 bg-gradient-to-br from-blue-600/[0.10] to-[#0D121C] p-5 mb-5 animate-fade-up">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center shrink-0"><Bot className="w-5 h-5 text-blue-300" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-wider font-mono-niq text-blue-300/80 mb-1">Analyst note</div>
                    <p className="text-sm sm:text-[15px] text-slate-200 leading-relaxed" data-testid="assistant-note-text">{meta.assistant_note}</p>
                    {(meta.insights?.top_sectors?.length > 0) && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {meta.insights.top_sectors.slice(0, 4).map((s) => (
                          <span key={s.name} className="text-xs font-mono-niq text-slate-300 bg-[#111726] border border-[#27334D] px-2.5 py-1 rounded-full">{s.name} · {s.count}</span>
                        ))}
                      </div>
                    )}
                    {meta.insights?.adjacent?.filter((a) => a.segment).length > 0 && (
                      <div className="mt-4 space-y-2" data-testid="segment-exports">
                        {meta.insights.adjacent.filter((a) => a.segment).map((a) => (
                          <div key={a.segment} className="flex flex-wrap items-center gap-2 text-xs text-slate-400 bg-[#0D121C] border border-[#1E2A3F] rounded-lg px-3 py-2">
                            <span className="flex-1 min-w-[140px]"><span className="text-slate-200 font-semibold font-mono-niq">{a.count.toLocaleString()}</span> {a.label}</span>
                            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono-niq">Export segment</span>
                            <button onClick={() => exportSegment(a.segment, "pdf")} disabled={!!segExporting} data-testid={`segment-export-pdf-${a.segment}`} className="flex items-center gap-1 px-2 py-1 rounded-md border border-[#27334D] hover:border-slate-500 transition-colors">{!isPaid ? <Lock className="w-3 h-3 text-slate-500" /> : segExporting === `${a.segment}-pdf` ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3 text-red-400" />} PDF</button>
                            <button onClick={() => exportSegment(a.segment, "excel")} disabled={!!segExporting} data-testid={`segment-export-excel-${a.segment}`} className="flex items-center gap-1 px-2 py-1 rounded-md border border-[#27334D] hover:border-slate-500 transition-colors">{!isPaid ? <Lock className="w-3 h-3 text-slate-500" /> : segExporting === `${a.segment}-excel` ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileSpreadsheet className="w-3 h-3 text-emerald-400" />} Excel</button>
                          </div>
                        ))}
                      </div>
                    )}
                    {meta.suggestions?.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-[#1E2A3F]">
                        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-mono-niq text-slate-500 mb-2"><Lightbulb className="w-3.5 h-3.5 text-amber-400" /> You may also want</div>
                        <div className="flex flex-wrap gap-2">
                          {meta.suggestions.map((s) => (
                            <button key={s} onClick={() => run(s)} data-testid="suggestion-chip" className="text-xs px-3 py-1.5 rounded-full border border-blue-500/30 bg-blue-600/10 text-blue-200 hover:bg-blue-600/20 hover:border-blue-500/50 transition-colors text-left">{s}</button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <span className="label-mono text-yellow-400 text-sm sm:text-base" data-testid="search-result-count">{results.length} match{results.length !== 1 ? "es" : ""}{lastRegion ? ` in ${lastRegion}` : ""}</span>
              {results.length > 0 && (
                <div className="flex gap-2">
                  <button onClick={saveCurrent} disabled={savingSearch} data-testid="save-search-button" className="flex items-center gap-1.5 text-xs border border-[#27334D] hover:border-blue-500/60 px-3 py-2 rounded-lg transition-colors">{savingSearch ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BookmarkPlus className="w-3.5 h-3.5 text-blue-400" />} Save</button>
                  <button onClick={() => doExport("pdf")} disabled={exporting} data-testid="export-pdf-button" className="flex items-center gap-1.5 text-xs border border-[#27334D] hover:border-slate-500 px-3 py-2 rounded-lg transition-colors">{!isPaid ? <Lock className="w-3.5 h-3.5 text-slate-500" /> : exporting === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 text-red-400" />} PDF</button>
                  <button onClick={() => doExport("excel")} disabled={exporting} data-testid="export-excel-button" className="flex items-center gap-1.5 text-xs border border-[#27334D] hover:border-slate-500 px-3 py-2 rounded-lg transition-colors">{!isPaid ? <Lock className="w-3.5 h-3.5 text-slate-500" /> : exporting === "excel" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-400" />} Excel</button>
                </div>
              )}
            </div>

            {results.length === 0 ? (
              <div className="text-center py-16 text-slate-500 rounded-2xl border border-[#1E2A3F] bg-[#111726]">No confident matches for "{lastQuery}". Try different wording.</div>
            ) : (
              <div className="space-y-3">
                {selected.length > 0 && (
                  <div className="sticky top-2 z-20 flex flex-wrap items-center gap-2 rounded-xl border border-blue-500/40 bg-[#0E1524]/95 backdrop-blur px-4 py-3 mb-1 shadow-lg shadow-blue-950/30" data-testid="selection-bar">
                    <span className="text-sm font-medium text-blue-200" data-testid="selected-count">{selected.length} selected</span>
                    <button onClick={clearSelected} data-testid="selection-clear" className="text-xs text-slate-400 hover:text-slate-200 underline">clear</button>
                    <div className="flex-1" />
                    <Popover>
                      <PopoverTrigger asChild>
                        <button data-testid="add-to-list-trigger" className="flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-500 px-3 py-2 rounded-lg font-medium transition-colors"><ListPlus className="w-3.5 h-3.5" /> Add to list</button>
                      </PopoverTrigger>
                      <PopoverContent align="end" className="w-64 p-0 bg-[#111726] border-[#27334D] text-slate-100">
                        <div className="px-3 py-2.5 border-b border-[#27334D] text-xs uppercase tracking-wider font-mono-niq text-slate-400">Add {selected.length} to…</div>
                        <div className="max-h-56 overflow-y-auto p-1.5">
                          {lists.length === 0 && <div className="px-2.5 py-3 text-xs text-slate-500">No lists yet. Create one below.</div>}
                          {lists.map((l) => (
                            <button key={l.id} onClick={() => addToList(l.id)} data-testid={`add-to-list-${l.id}`} className="w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-md hover:bg-white/[0.04] text-sm text-left">
                              <span className="truncate">{l.name}</span>
                              <span className="text-xs text-slate-500 font-mono-niq shrink-0">{l.count}</span>
                            </button>
                          ))}
                        </div>
                        <div className="p-2 border-t border-[#27334D]">
                          {addingList ? (
                            <div className="flex items-center gap-1.5">
                              <input autoFocus value={newListName} onChange={(e) => setNewListName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createAndAdd()} data-testid="new-list-inline-input" placeholder="New list name…" className="flex-1 bg-[#0D121C] border border-[#27334D] rounded-md px-2 py-1.5 text-sm outline-none focus:border-blue-500" />
                              <button onClick={createAndAdd} data-testid="new-list-inline-save" className="bg-blue-600 hover:bg-blue-500 px-2.5 py-1.5 rounded-md text-xs">Add</button>
                            </div>
                          ) : (
                            <button onClick={() => setAddingList(true)} data-testid="new-list-inline-trigger" className="w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-sm text-blue-400 hover:bg-white/[0.04]"><FolderPlus className="w-4 h-4" /> New list</button>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                    <button onClick={() => exportSelected("pdf")} disabled={!!exporting} data-testid="export-selected-pdf" className="flex items-center gap-1.5 text-xs border border-[#27334D] hover:border-slate-500 px-3 py-2 rounded-lg transition-colors">{!isPaid ? <Lock className="w-3.5 h-3.5 text-slate-500" /> : exporting === "sel-pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 text-red-400" />} PDF</button>
                    <button onClick={() => exportSelected("excel")} disabled={!!exporting} data-testid="export-selected-excel" className="flex items-center gap-1.5 text-xs border border-[#27334D] hover:border-slate-500 px-3 py-2 rounded-lg transition-colors">{!isPaid ? <Lock className="w-3.5 h-3.5 text-slate-500" /> : exporting === "sel-excel" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-400" />} Excel</button>
                  </div>
                )}
                <div className="rounded-xl border border-[#1E2A3F] bg-[#0D121C] px-4 py-3 mb-1">
                  <ConfidenceLegend />
                </div>
                {results.map((r, i) => (
                  <div key={r.id} data-testid={`search-result-${r.id}`} className={`rounded-xl border ${selected.includes(r.id) ? "border-blue-500/60 bg-blue-600/[0.06]" : "border-[#27334D] bg-[#111726] hover:border-blue-500/30"} p-5 transition-colors animate-fade-up`} style={{ animationDelay: `${Math.min(i, 12) * 0.04}s` }}>
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex items-center gap-3">
                        <Checkbox checked={selected.includes(r.id)} onCheckedChange={() => toggleSelect(r.id)} data-testid={`select-result-${r.id}`} className="border-slate-500 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 shrink-0" />
                        <div className="w-9 h-9 rounded-full bg-blue-600/15 border border-blue-500/25 flex items-center justify-center text-sm font-semibold text-blue-300 shrink-0">{(r.first_name || "?")[0]}</div>
                        <div>
                          {r.profile_url ? (
                            <a href={r.profile_url} target="_blank" rel="noopener noreferrer" data-testid={`result-linkedin-${r.id}`}
                              className="font-medium inline-flex items-center gap-1.5 text-slate-100 hover:text-blue-400 transition-colors group">
                              {r.first_name} {r.last_name}
                              <ExternalLink className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </a>
                          ) : (
                            <div className="font-medium">{r.first_name} {r.last_name}</div>
                          )}
                          <div className="text-xs text-slate-500">{r.position} · {r.normalized_company || r.company}</div>
                          {r.email && <a href={`mailto:${r.email}`} data-testid={`result-email-${r.id}`} className="text-xs text-slate-400 hover:text-blue-400 transition-colors">{r.email}</a>}
                        </div>
                      </div>
                      <MatchBadge level={r.match} />
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed border-l-2 border-blue-500/40 pl-3 mb-3">{r.reason}</p>
                    <div className="flex flex-wrap gap-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1.5">{r.sector} <ConfidenceBadge level={r.sector_confidence} /></span>
                      <span className="flex items-center gap-1.5">{r.seniority} <ConfidenceBadge level={r.seniority_confidence} /></span>
                      <span className="flex items-center gap-1.5">{r.region} <ConfidenceBadge level={r.region_confidence} /></span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
